"use client"

import { useState } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { CreateStatus } from "@/constants/status";
import { useForm } from "react-hook-form";
import { SettingsSchema } from "@/schemas/settings.schema";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "../ui/input";
import { SettingsFormData, SettingsFormSchema } from "@/schemas/settings-form.schema";
import { TextArea } from "../ui/textarea";
import { dropdownCss } from "../ui/dropdown";
import GlobalCssStrings from "@/app/globalCssStrings";
import { cn } from "@/lib/utils";
import { createSetting } from "@/services/settings.service";

interface Props {
    updateData: (data: z.infer<typeof SettingsSchema>) => void,
}
export default function SettingsModal(props: Props){
    const [open, setOpen] = useState(false);
    const [createStatus, setCreateStatus] = useState(CreateStatus.standBy);
    const [failedMessage, setFailedMessage] = useState("");

    const typeChoices = [
        'Text',
        'Textarea',
        'Foto',
        'Email',
        'Keywords',
        'Boolean',
        'Number Slider',
        'Number'
    ];

    const sectionChoices = [
        'WEBSITE',
        'API',
        'DOMAIN',
        'ADMIN',
        'CLEARBOOK',
    ];

    const form = useForm<z.infer<typeof SettingsFormSchema>>({
        resolver: zodResolver(SettingsFormSchema),
        defaultValues: {
            Name:"",
            Label: "",
            Value: "",
            Backup: "",
            Description: "",
            Type: "0",
            Section: "WEBSITE",
        },
    });

    const CreateSettings = async (data: z.infer<typeof SettingsFormSchema>) => {
        setCreateStatus(CreateStatus.onGoing);
        const convertedData = SettingsFormData(data);
        let result = null
        try {
            result = await createSetting(convertedData);
            setCreateStatus(CreateStatus.completed);
        } catch (error) {
            setCreateStatus(CreateStatus.failed);
            errorHandler(error);
        }
        if (result) {
            handleSuccess(result);
        }
        return;
    }

    const errorHandler = (data: unknown) => {
        console.log("Erorr occoured");
        console.log(data);
        setFailedMessage("An error occurred while creating the setting.");
    }
    
    const handleSuccess = (data: z.infer<typeof SettingsSchema>) => {
        props.updateData(data);
        setOpen(false);
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer">Create Setting</Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogTitle>Create Setting</DialogTitle>
                <DialogDescription> 
                </DialogDescription>
                <div>
                    {(createStatus === CreateStatus.standBy ||
                        createStatus === CreateStatus.notAuthroized) && (
                            <Form {...form}>
                                <form 
                                    autoComplete="off"
                                    className="grid"
                                    onSubmit={form.handleSubmit(CreateSettings, errorHandler)}
                                >
                                        <div className="col-start-1 col-end-4 rounded-md border-black">
                                           <FormField
                                                control={form.control}
                                                name="Name"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2 w-full">
                                                        <FormLabel>Name</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Name"
                                                            {...field}
                                                            className={GlobalCssStrings.InputCSS}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="Label"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Label</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Label"
                                                            {...field}
                                                            className={GlobalCssStrings.InputCSS}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="Value"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Value</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Value"
                                                            {...field}
                                                            className={GlobalCssStrings.InputCSS}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="Description"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Description</FormLabel>
                                                        <FormControl>
                                                            <TextArea
                                                                cols={30}
                                                                rows={4}
                                                                placeholder="Description"
                                                                className={cn(GlobalCssStrings.InputCSS, "form-control form-control-sm h-30")}
                                                                {...field}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <div className="col-start-4 col-end-7 rounded-md border-black ms-2">
                                            <FormField
                                                control={form.control}
                                                name="Type"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Type</FormLabel>
                                                        <FormControl>
                                                            <select id="setting_type" className={cn(dropdownCss, "mb-0")} {...field}>
                                                                {typeChoices.map((option, index) => (
                                                                    <option
                                                                        key={index}
                                                                        value={index}
                                                                        className="block px-4 py-2
                                                                            text-sm text-black
                                                                            hover:bg-gray-100"
                                                                        onClick={() => {}}
                                                                    >
                                                                        {option}
                                                                    </option>
                                                                    ))}
                                                            </select>
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="Section"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Section</FormLabel>
                                                        <FormControl>
                                                            <select id="setting_section" className={cn(dropdownCss, "mb-0")} {...field}>
                                                                {sectionChoices.map((option, index) => (
                                                                    <option
                                                                        key={index}
                                                                        value={option}
                                                                        className="block px-4 py-2
                                                                            text-sm text-black
                                                                            hover:bg-gray-100"
                                                                        onClick={() => {}}
                                                                    >
                                                                        {option}
                                                                    </option>
                                                                    ))}
                                                            </select>
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="Backup"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Backup</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Backup"
                                                            {...field}
                                                            className={GlobalCssStrings.InputCSS}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    <div className="w-full">
                                        {createStatus == CreateStatus.notAuthroized && (
                                        <h1 className="w-full text-xl font-bold text-black dark:text-white mb-2 py-1 px-2 rounded-md bg-red-500">
                                            Authorization to deploy failed
                                        </h1>
                                        )}
                                        <Button
                                            className="w-full mt-4 cursor-pointer"
                                            type="submit"
                                        >
                                            Create Setting
                                        </Button>
                                    </div>
                                </form>
                            </Form>
                    )}
                </div>
                <DialogFooter>
                    <div className="grid grid-cols-6 gap-4">
                        <div className="col-start-1 col-end-7 rounded-md border-black">
                            {createStatus == CreateStatus.onGoing && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Creating Entry...</p>
                                </div>
                            )}
                            {createStatus == CreateStatus.completed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Created Entry! Will refresh this page momentarily</p>
                                </div>
                            )}
                            {createStatus == CreateStatus.failed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>{failedMessage}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}